import { Card, Header } from "../molecules";
import "../../resources/home.scss";
import {
  BankOutlined,
  BellOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TransactionOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useAppSelector } from "../../app/hooks";
import { userState } from "../../store/slices/user";
import { transactionState } from "../../store/slices/transaction";
import {
  TransactionType,
  TransactionTypeEnum,
} from "../../constants/transaction";
import { useMemo } from "react";
import {
  generateRandomColor,
  getLastSixMonths,
  getMonthlyTotal,
} from "../../utils";
import moment from "moment";

export const Home = () => {
  const user = useAppSelector(userState);
  const { transactions, income, expense, transfer } =
    useAppSelector(transactionState);

  const spendingData = useMemo(() => {
    const months = getLastSixMonths();

    return months?.map((month: string) => {
      const expensesMonth = transactions
        ?.filter((transaction: TransactionType) => {
          const date = moment(transaction.createdAt);
          return (
            transaction?.type === TransactionTypeEnum.EXPENSE &&
            date.format("MMM") === month &&
            date.year() === moment().year()
          );
        })
        ?.reduce((acc, curr) => acc + curr.amount, 0);

      return { month, amount: expensesMonth };
    });
  }, [transactions]);

  const expenseBreakdown = useMemo(() => {
    const categories: string[] = [];

    transactions.forEach((transaction: TransactionType) => {
      if (!categories?.includes(transaction?.category)) {
        categories.push(transaction?.category);
      }
    });

    return categories?.map((category: string) => {
      const transactionsWithCategory: Array<TransactionType> =
        transactions?.filter((t: TransactionType) => t.category === category);

      return {
        name: category,
        value: transactionsWithCategory?.reduce(
          (acc, curr) => acc + curr.amount,
          0,
        ),
        color: generateRandomColor(),
      };
    });
  }, [transactions]);

  const percentChange = useMemo(() => {
    const currentMonth = 4;
    const lastMonth = 3;

    console.log("here currentMonth", currentMonth);
    console.log("here lastMonth", lastMonth);

    const currentIncome = getMonthlyTotal(
      transactions,
      currentMonth,
      TransactionTypeEnum.INCOME,
    );
    const currentExpense = getMonthlyTotal(
      transactions,
      currentMonth,
      TransactionTypeEnum.EXPENSE,
    );
    const currentBalance = currentIncome - currentExpense;

    const lastIncome = getMonthlyTotal(
      transactions,
      lastMonth,
      TransactionTypeEnum.INCOME,
    );
    const lastExpense = getMonthlyTotal(
      transactions,
      lastMonth,
      TransactionTypeEnum.EXPENSE,
    );
    const lastBalance = lastIncome - lastExpense;

    console.log("here currentBalance", currentBalance);
    console.log("here lastBalance", lastBalance);

    return lastBalance === 0
      ? null
      : ((currentBalance - lastBalance) / Math.abs(lastBalance)) * 100;
  }, [transactions]);

  return (
    <div className="cp-home">
      <Header />

      <div className="cp-home__container">
        {/* Welcome Section */}
        <div className="cp-home__welcome">
          <h1>Welcome back, {user?.firstName}! 👋</h1>
          <p>Here's your financial overview for today</p>
        </div>

        {/* Account Balance */}
        <Card>
          <div className="cp-home__container__account-balance">
            <h2>Account Balance</h2>
            <p>Total balance in your account</p>
            <h1>{(income - expense)?.toLocaleString("en-Us")}</h1>
            <div
              className={`cp-home__balance-change cp-home__balance-change--${percentChange && percentChange > 0 ? "positive" : "negative"}`}
            >
              {!percentChange || percentChange < 0 ? (
                <ArrowDownOutlined />
              ) : (
                <ArrowUpOutlined />
              )}
              <span>
                {!percentChange
                  ? "N/A"
                  : `${percentChange > 0 ? "+" : ""}${percentChange.toFixed(2)}%`}{" "}
                from last month
              </span>
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <div className="cp-home__quick-stats">
          <Card>
            <div className="cp-home__stat-card">
              <div className="cp-home__stat-icon income">
                <ArrowDownOutlined />
              </div>
              <div className="cp-home__stat-content">
                <h3>${income.toLocaleString("en-Us")}</h3>
                <p>Monthly Income</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="cp-home__stat-card">
              <div className="cp-home__stat-icon expense">
                <ArrowUpOutlined />
              </div>
              <div className="cp-home__stat-content">
                <h3>${expense?.toLocaleString("en-Us")}</h3>
                <p>Monthly Expenses</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="cp-home__stat-card">
              <div className="cp-home__stat-icon savings">
                <DollarOutlined />
              </div>
              <div className="cp-home__stat-content">
                <h3>${transfer?.toLocaleString("en-Us")}</h3>
                <p>Net Savings</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="cp-home__container__stats">
          <Card>
            <h3>Recent Activity</h3>
            <ul>
              {transactions
                ?.slice(0, 3)
                .map((activity: TransactionType, index) => {
                  let icon = <BankOutlined />;
                  if (activity.type === TransactionTypeEnum.INCOME) {
                    icon = <BellOutlined />;
                  }
                  if (activity.type === TransactionTypeEnum.TRANSFER) {
                    icon = <TransactionOutlined />;
                  }

                  return (
                    <li
                      key={index}
                      style={{ "--index": index } as React.CSSProperties}
                    >
                      <span className="cp-home__container__stats__activities">
                        {icon} <p>{activity.category}</p>
                      </span>
                      <p
                        className={`cp-home__activity-amount ${activity.type}`}
                      >
                        {activity.type === TransactionTypeEnum.EXPENSE
                          ? "-"
                          : "+"}
                        ${activity.amount}
                      </p>
                    </li>
                  );
                })}
            </ul>
          </Card>

          <Card>
            <h3>Spending Trends</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={spendingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#9fd6de"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Expense Breakdown */}
        <Card>
          <h3>Expense Breakdown</h3>
          <div className="cp-home__expense-breakdown">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={expenseBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {expenseBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `$${value?.toLocaleString("en-Us")}`}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="cp-home__breakdown-legend">
              {expenseBreakdown.map((item, index) => (
                <div key={index} className="cp-home__legend-item">
                  <div
                    className="cp-home__legend-color"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span>
                    {item.name}: ${item.value.toLocaleString("en-Us")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Home;
